import React, { useState, useRef, useEffect } from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { createPortal } from "react-dom";

const UaucOpenDetailHelpDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen((s) => !s);
  const drawerContentRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (drawerContentRef.current && !drawerContentRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const drawer = (
    <div className="drawer-portal">
      <Drawer
        open={isOpen}
        onClose={toggle}
        direction="right"
        size={500}
        duration={400}
        overlayOpacity={0.5}
        overlayColor="#000"
        customIdSuffix="help-guide-open-detail"
        className="help-guide-drawer"
        style={{ top: "73px", height: "calc(100vh - 73px)", transition: "all 0.4s ease-in-out" }}
      >
      <div class="drawer-content">
  <div class="drawer-header">
    <div class="header-title">
      <span class="emoji">📝</span>
      <span class="title">UAUC Open Detail Page – Help Guide</span>
    </div>
    <button class="close-button" onClick={toggle} aria-label="Close help panel">&times;</button>
  </div>

  <div class="drawer-body">
    <p>
      The UAUC Detail Page provides a comprehensive view of a reported Unsafe Act (UA) or Unsafe Condition (UC), 
      along with related information, evidence, assignments, escalations, and team collaboration tools.
    </p>

    <h5 class="section-title">📌 Header Information</h5>
    <hr />
    <p>At the top, you will see:</p>
    <ul class="steps-list">
      <li>. Record Number (e.g., 940)</li>
      <li>. UAUC Type (Unsafe Act or Unsafe Condition)</li>
      <li>. Activity Name (e.g., Bar Bending)</li>
      <li>. Reported Month (e.g., July)</li>
    </ul>

    <p>Below this, a timeline view displays:</p>
    <ul class="steps-list">
      <li>. Reported Details</li>
      <li>. Assigned To Details</li>
      <li>. Overdue Details</li>
      <li>. Invited Users</li>
      <li>. Latest Comment</li>
    </ul>

    <h5 class="section-title">📝 Reported Details</h5>
    <hr />
    <p>This section captures key details of the reported UAUC:</p>
    <ul class="steps-list">
      <li>. Evidence (uploaded image of the UAUC)</li>
      <li>. Observation (description of the unsafe act or condition)</li>
      <li>. Hazard Type</li>
      <li>. Risk Level</li>
      <li>. Source (Rules) under which the violation occurred</li>
    </ul>

    <h5 class="section-title">🏗️ Activity Info</h5>
    <hr />
    <p>Provides contextual details about where the UAUC was reported:</p>
    <ul class="steps-list">
      <li>. Service and its respective Plant / Project / Client</li>
      <li>. Barrier(s) captured during reporting</li>
      <li>. Sub-Hazard linked to the hazard type</li>
      <li>. Department / Contractor / Location associated with the UAUC</li>
    </ul>

    <h5 class="section-title">🛡️ Control Measures</h5>
    <hr />
    <p>Displays the list of missing or violated control measures that were not used during the activity.</p>

    <h5 class="section-title">👥 Assigned Details & Team Collaboration</h5>
    <hr />
    <ul class="steps-list">
      <li>👤 <b>Assigned User:</b> The person responsible for resolving the UAUC</li>
      <li>👥 <b>Invited Team Members:</b> Users added to collaborate on resolution</li>
      <li>➡️ To invite: click the Invite User icon (top bar) → select team members in the pop-up → notifications will be sent</li>
    </ul>

    <h5 class="section-title">🚫 Violator Details</h5>
    <hr />
    <p>Names of violators involved in the unsafe act/condition (captured during reporting).</p>

    <h5 class="section-title">📂 Document Section</h5>
    <hr />
    <p>Allows uploading of supporting documents.</p>
    <ul class="steps-list">
      <li>📄 Click <b>Add Document</b> → select and upload files.</li>
    </ul>

    <h5 class="section-title">⚠️ Escalation Details</h5>
    <hr />
    <p>Displays escalation levels if a UAUC is not resolved on/before the due date.</p>
    <p><i>Example:</i> L1 indicates the first escalation level as per the escalation matrix (shown in org-chart format).</p>

    <h5 class="section-title">⏳ Overdue Section</h5>
    <hr />
    <p>Shows the number of days overdue if the UAUC resolution exceeds the due date.</p>

    <h5 class="section-title">💬 Discussion Section</h5>
    <hr />
    <p>A chat-based collaboration space for team members.</p>
    <p><b>Purpose:</b></p>
    <ul class="steps-list">
      <li>. Discuss resolution approaches</li>
      <li>. Plan risk reduction strategies</li>
      <li>. Share ideas for long-term prevention of incidents/injuries</li>
    </ul>

    <h5 class="section-title">📊 Analysis Panel (Right-Hand Side)</h5>
    <hr />
    <p>Provides detailed analytics related to the activity:</p>
    <ul class="steps-list">
      <li>. IRA Analysis (accident statistics)</li>
      <li>. Near-Miss Analysis</li>
      <li>. UA vs UC Analysis</li>
      <li>. Risk-Based Analysis</li>
      <li>. Good Act & TBT Count</li>
      <li>. Internal & External Training Count</li>
      <li>. Employee & Contractor Training Count</li>
    </ul>
    <p>This data helps in building data-driven strategies to reduce future UAUC incidents.</p>

    <h5 class="section-title">✏️ Editing UAUC</h5>
    <hr />
    <p>
      Click the <b>Edit</b> icon (top right corner) to modify a UAUC.
      <br />
      <i>Note:</i> Only the user who reported the UAUC can edit their own records.
    </p>
  </div>
</div>


      </Drawer>
    </div>
  );

  return (
    <>
      <div
        className="toggle-help-btn"
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === "Enter" || e.key === " ") toggle();
        }}
        style={{ fontSize: "16px", cursor: "pointer" }}
      >
        Page Help
        <img src="/images/formkit_help.svg" alt="" />
      </div>
      {createPortal(drawer, document.body)}
    </>
  );
};

export default UaucOpenDetailHelpDrawer;


